import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import Landing from '@/pages/Landing';
import Auth from '@/pages/Auth';
import Index from '@/pages/Index';
import Documentation from '@/pages/Documentation';
import NotFound from '@/pages/NotFound';
import Settings from '@/pages/Settings';
import ModernSettings from '@/pages/ModernSettings';
import PortfolioOverviewPage from '@/pages/PortfolioOverviewPage';
import './App.css';
import { supabase } from './integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ChatHistory } from '@/components/chat/ChatHistory';
import { TradingHistory } from '@/components/trading/TradingHistory';
import { Compliance } from '@/components/compliance/Compliance';
import MarketAnalysis from './pages/MarketAnalysis';
import { BloombergRadioProvider } from './contexts/BloombergRadioContext';
import { TradingComponentsProvider } from './components/trading/components-provider';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { UploadProvider } from './contexts/UploadContext';
import { Navigation } from '@/components/layout/Navigation';
import { Sidebar } from '@/components/layout/Sidebar';
import { Button } from '@/components/ui/button';
import Studies from '@/pages/Studies';
// Import the new PortfolioOverview component instead of the old page
import { PortfolioOverview } from '../components/portfolio/index';
import Analytics from '@/pages/Analytics';
import BacktestHistory from '@/pages/BacktestHistory';
import { VMEndpointTester } from '@/components/trading/VMEndpointTester';
import TradingBackendTest from '@/pages/TradingBackendTest';


// Import investor portal components
const InvestorPortal = React.lazy(() => import('../app/investor/page'));
const InvestorDashboard = React.lazy(() => import('../app/investor/dashboard'));

function App() {
  return (
    <UploadProvider>
      <BloombergRadioProvider>
        <TradingComponentsProvider>
          <Router>
          <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/docs" element={<Documentation />} />
          <Route path="/docs/:slug" element={
            <React.Suspense fallback={<div className="min-h-screen bg-gray-900 flex items-center justify-center">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
            </div>}>
              {React.createElement(React.lazy(() => import('@/pages/DocArticlePage')))}
            </React.Suspense>
          } />
          <Route path="/blog/:slug" element={
            <React.Suspense fallback={<div className="min-h-screen bg-gray-900 flex items-center justify-center">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
            </div>}>
              {React.createElement(React.lazy(() => import('@/pages/BlogPostPage')))}
            </React.Suspense>
          } />
          <Route path="/studies" element={<Studies />} />

          {/* Investor Portal Routes */}
          <Route path="/investor" element={
            <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
            </div>}>
              <InvestorPortal />
            </React.Suspense>
          } />
          <Route path="/investor/dashboard" element={
            <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
            </div>}>
              <InvestorDashboard />
            </React.Suspense>
          } />

          {/* Protected Routes (Main Trading App) */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Index />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat-history"
            element={
              <ProtectedRoute>
                <ChatHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trading-history"
            element={
              <ProtectedRoute>
                <TradingHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/backtest-history"
            element={
              <ProtectedRoute>
                <BacktestHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <Analytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/compliance"
            element={
              <ProtectedRoute>
                <Compliance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/market-analysis"
            element={
              <ProtectedRoute>
                <MarketAnalysis />
              </ProtectedRoute>
            }
          />
          
          {/* Market Analysis Routes */}
          <Route
            path="/analysis"
            element={
              <ProtectedRoute>
                <MarketAnalysis />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analysis/:symbol"
            element={
              <ProtectedRoute>
                <MarketAnalysis />
              </ProtectedRoute>
            }
          />
          
          {/* Original Settings Route */}
          <Route
            path="/settings-original"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          
          {/* New Redesigned Settings Route */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <ModernSettings />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/vm-tester"
            element={
              <ProtectedRoute>
                <div className="min-h-screen bg-gray-50 p-6">
                  <div className="max-w-7xl mx-auto">
                    <div className="mb-6">
                      <h1 className="text-3xl font-bold text-gray-900">VM Endpoint Tester</h1>
                      <p className="text-gray-600 mt-2">Test and configure your Google VM trading endpoints</p>
                    </div>
                    <VMEndpointTester />
                  </div>
                </div>
              </ProtectedRoute>
            }
          />
          <Route
            path="/backend-test"
            element={
              <ProtectedRoute>
                <TradingBackendTest />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/portfolio"
            element={
              <ProtectedRoute>
                <PortfolioOverviewPage />
              </ProtectedRoute>
            }
          />

          {/* Redirect common paths that might be used */}
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/login" element={<Navigate to="/auth" replace />} />
          <Route path="/signup" element={<Navigate to="/auth" replace />} />

          {/* 404 page */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Toaster />
        </Router>
      </TradingComponentsProvider>
    </BloombergRadioProvider>
    </UploadProvider>
  );
}

export default App;